import { toast } from "sonner";
import { Coins, ShieldCheck, Check, ArrowUpRight, ArrowDownRight, CreditCard } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { DEMO_FAN, TOKEN_PACKS, TRANSACTIONS, STUDIO } from "../constants/demoData";
import { Avatar } from "./DemoLayout";
import { CreatorChip } from "./DemoHome";

const money = (n) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => n.toLocaleString("es-CO");

const PageHead = ({ title, sub, testId }) => (
  <div className="mb-6">
    <h1 data-testid={testId} className="font-display text-3xl font-extrabold tracking-tight text-ink">{title}</h1>
    <p className="mt-1 text-sm text-mist">{sub}</p>
  </div>
);

const Stat = ({ label, value, accent = false, testId }) => (
  <div data-testid={testId} className={`card-soft p-5 ${accent ? "bg-night text-white" : ""}`} style={accent ? { background: "#12091f", borderColor: "#2a1745" } : {}}>
    <p className={`eyebrow ${accent ? "text-violet-300" : "text-mist"}`}>{label}</p>
    <p className={`font-display mt-2 text-2xl font-extrabold ${accent ? "text-white" : "text-ink"}`}>{value}</p>
  </div>
);

export const DemoWallet = () => {
  const { t, lang } = useLang();
  const w = t.demo.wallet;
  return (
    <div data-testid="demo-wallet" className="pb-20">
      <PageHead title={w.title} sub={w.sub} testId="demo-wallet-title" />
      <div className="grid gap-4 lg:grid-cols-12">
        <div className="hero-card grain relative overflow-hidden rounded-[1.5rem] border border-white/70 p-7 lg:col-span-5">
          <div className="relative">
            <p className="eyebrow text-violet-700">{w.balance}</p>
            <p data-testid="demo-wallet-balance" className="font-display mt-3 text-5xl font-extrabold tracking-tight text-ink"><Coins size={28} className="mr-2 inline text-amber-500" />{num(DEMO_FAN.tokens)}</p>
            <p className="mt-1 text-sm text-ink-2">{w.equivalent} {money(DEMO_FAN.usd)}</p>
            <button data-testid="demo-wallet-add" onClick={() => toast(t.demo.toastPay)} className="pill btn-primary mt-6 px-5 py-2.5 text-sm font-semibold">+ {w.addFunds}</button>
            <p className="mt-6 flex items-start gap-2 text-[0.7rem] leading-relaxed text-ink-2"><ShieldCheck size={14} className="mt-0.5 shrink-0 text-violet-600" />{w.disabled}</p>
          </div>
        </div>
        <div className="lg:col-span-7">
          <p className="eyebrow text-mist">{w.packs}</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {TOKEN_PACKS.map((p) => (
              <div key={p.tokens} data-testid={`demo-pack-${p.tokens}`} className="card-soft flex items-center justify-between p-4">
                <div><p className="font-display text-lg font-bold text-ink"><Coins size={15} className="mr-1 inline text-amber-500" />{num(p.tokens)}</p><p className="text-xs text-mist">{money(p.usd)} USD</p></div>
                <button onClick={() => toast(t.demo.toastPay)} className="pill btn-ghost inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold"><CreditCard size={13} /> {w.buy}</button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="eyebrow mt-10 text-mist">{w.history}</p>
      <div data-testid="demo-transactions" className="card-soft mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-lilac text-left text-[0.65rem] uppercase tracking-wider text-mist"><tr><th className="px-5 py-3">{w.date}</th><th className="px-5 py-3">{w.desc}</th><th className="px-5 py-3">{w.method}</th><th className="px-5 py-3 text-right">{w.amount}</th></tr></thead>
          <tbody className="divide-y divide-line">
            {TRANSACTIONS.map((x) => (
              <tr key={x.id} className="hover:bg-canvas">
                <td className="px-5 py-3 text-ink-2">{x.date}</td>
                <td className="px-5 py-3"><span className="mr-2 rounded-full bg-violet-100 px-2 py-0.5 text-[0.65rem] font-semibold text-violet-800">{w.kinds[x.kind]}</span><span className="text-ink">{x.desc[lang]}</span></td>
                <td className="px-5 py-3 text-xs text-mist">{x.kind === "unlock" ? "Tokens" : w.processor}</td>
                <td className="px-5 py-3 text-right font-semibold">
                  {x.tokens ? <span className={x.tokens > 0 ? "text-emerald-600" : "text-ink"}>{x.tokens > 0 ? <ArrowDownRight size={13} className="inline" /> : <ArrowUpRight size={13} className="inline" />} {x.tokens > 0 ? "+" : ""}{num(x.tokens)} tk</span> : null}
                  {x.usd ? <span className="ml-2 text-xs text-mist">{money(x.usd)} USD</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const DemoProfile = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const p = t.demo.profile;
  const rows = [[p.username, `@${user.username}`], [p.email, user.email], [p.role, p.demoRole], [p.member, DEMO_FAN.memberSince], [p.lang, lang.toUpperCase()], [p.status, <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck size={14} /> {p.verified}</span>]];
  return (
    <div data-testid="demo-profile" className="pb-20">
      <PageHead title={p.title} sub={p.sub} testId="demo-profile-title" />
      <div className="card-soft overflow-hidden">
        <div className="hero-card h-28" />
        <div className="px-6 pb-6">
          <Avatar size="-mt-8 h-16 w-16 text-lg" className="border-4 border-white" />
          <p className="font-display mt-3 text-xl font-extrabold text-ink">{user.display_name}</p>
          <p className="text-xs text-mist">@{user.username}</p>
          <dl className="mt-6 divide-y divide-line border-t border-line">
            {rows.map(([k, v], i) => <div key={i} className="grid gap-1 py-3 sm:grid-cols-3"><dt className="text-xs font-semibold uppercase tracking-wider text-mist">{k}</dt><dd className="text-sm text-ink sm:col-span-2">{v}</dd></div>)}
          </dl>
          <button className="pill btn-ghost mt-4 px-5 py-2 text-xs font-semibold">{p.edit}</button>
        </div>
      </div>
    </div>
  );
};

export const DemoSecurity = () => {
  const { t } = useLang();
  const s = t.demo.security;
  return (
    <div data-testid="demo-security" className="pb-20">
      <PageHead title={s.title} sub={s.sub} testId="demo-security-title" />
      <div className="grid gap-3 md:grid-cols-2">
        {s.items.map((it) => (
          <div key={it} className="card-soft flex items-center justify-between p-4">
            <span className="flex items-center gap-3 text-sm text-ink"><span className="flex h-8 w-8 items-center justify-center rounded-xl bg-violet-100 text-violet-700"><Check size={14} /></span>{it}</span>
            <span className="h-5 w-9 rounded-full bg-violet-600 p-0.5"><span className="block h-4 w-4 translate-x-4 rounded-full bg-white" /></span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DemoStudio = () => {
  const { t, lang } = useLang();
  const s = t.demo.studio;
  return (
    <div data-testid="demo-studio" className="pb-20">
      <PageHead title={s.title} sub={s.sub} testId="demo-studio-title" />
      <div className="mb-4 flex items-center gap-3"><CreatorChip c={STUDIO.creator} /><span className="rounded-full bg-lilac px-2 py-0.5 text-[0.65rem] font-semibold text-ink-2">{t.demo.nav.creatorView}</span></div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label={s.earnings} value={money(STUDIO.earnings30)} accent testId="demo-studio-earnings" />
        <Stat label={s.subscribers} value={num(STUDIO.subscribers)} />
        <Stat label={s.unlocks} value={num(STUDIO.unlocks30)} />
        <Stat label={s.available} value={money(STUDIO.available)} />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-12">
        <div className="card-soft p-6 lg:col-span-5">
          <p className="eyebrow text-mist">{s.split}</p>
          <div className="mt-4 flex h-4 overflow-hidden rounded-full"><div className="bg-gradient-to-r from-violet-600 to-fuchsia-500" style={{ width: `${STUDIO.feeCreator}%` }} /><div className="bg-pink-200" style={{ width: `${STUDIO.feePlatform}%` }} /></div>
          <div className="mt-3 flex justify-between text-xs"><span className="font-semibold text-ink">{STUDIO.feeCreator}% {s.creator}</span><span className="text-ink-2">{STUDIO.feePlatform}% {s.platform}</span></div>
          <p className="mt-4 text-xs leading-relaxed text-ink-2"><strong>{STUDIO.feeCreator}%</strong> {s.splitNote}</p>
          <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
            <div className="rounded-xl bg-lilac p-3"><p className="text-mist">{s.pending}</p><p className="font-display mt-1 text-base font-bold text-ink">{money(STUDIO.pending)}</p><p className="text-[0.65rem] text-mist">{STUDIO.holdingDays} {s.holding}</p></div>
            <div className="rounded-xl bg-lilac p-3"><p className="text-mist">{s.nextPayout}</p><p className="font-display mt-1 text-base font-bold text-ink">{STUDIO.nextPayout}</p></div>
          </div>
          <button data-testid="demo-studio-payout" onClick={() => toast(t.demo.toastPay)} className="pill btn-primary mt-5 w-full py-2.5 text-xs font-semibold">{s.request}</button>
        </div>
        <div className="lg:col-span-7">
          <div className="card-soft overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-lilac text-left text-[0.65rem] uppercase tracking-wider text-mist"><tr><th className="px-5 py-3">{s.top}</th><th className="px-5 py-3">{s.type}</th><th className="px-5 py-3 text-right">{s.sales}</th><th className="px-5 py-3 text-right">{s.revenue}</th></tr></thead>
              <tbody className="divide-y divide-line">{STUDIO.top.map((r, i) => <tr key={i}><td className="px-5 py-3 font-medium text-ink">{r.title[lang]}</td><td className="px-5 py-3"><span className="rounded-full bg-violet-100 px-2 py-0.5 text-[0.65rem] font-semibold text-violet-800">{t.demo.types[r.type]}</span></td><td className="px-5 py-3 text-right text-ink-2">{num(r.sales)}</td><td className="px-5 py-3 text-right font-semibold text-ink">{money(r.revenue)}</td></tr>)}</tbody>
            </table>
          </div>
          <div className="card-soft mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-lilac text-left text-[0.65rem] uppercase tracking-wider text-mist"><tr><th className="px-5 py-3">{s.payouts}</th><th className="px-5 py-3 text-right">USD</th><th className="px-5 py-3 text-right">Status</th></tr></thead>
              <tbody className="divide-y divide-line">{STUDIO.payouts.map((p) => <tr key={p.date}><td className="px-5 py-3 text-ink-2">{p.date}</td><td className="px-5 py-3 text-right font-semibold text-ink">{money(p.usd)}</td><td className="px-5 py-3 text-right"><span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[0.65rem] font-semibold text-emerald-700">✓ {s.paid}</span></td></tr>)}</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
